import { collection, doc, documentId, endBefore, getCountFromServer, getDoc, getDocs, limit, orderBy, query, setDoc, startAfter, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { firestore } from "../../services/firebase/index";

const functions = getFunctions();

/**
 * Um lote da varredura completa de alunos (usada nos cards de estatísticas e no
 * diretório de busca por nome).
 *
 * Ordena por ID do documento, e NÃO por `Email` como o `getUsers` da tabela: no
 * Firestore, `orderBy` exclui do resultado todo documento que não tem o campo —
 * alunos sem e-mail sumiam da contagem, e a soma dos cards não fechava com o
 * total. O ID sempre existe, então a paginação é completa e confiável.
 */
export const getUsersScanBatch = async ({ pageReference, pageSize }) => {
    const constraints = [orderBy(documentId()), limit(pageSize)];
    if (pageReference) constraints.push(startAfter(pageReference));
    return getDocs(query(collection(firestore, "users"), ...constraints));
};

export const getUsers = async (
    {
        pageReference,
        pageSize,
        isNextPage,
        email,
    }
) => {
    const usersCollection = collection(firestore, 'users');

    const buildQueries = (emailFilter) => {
        const queries = [
            orderBy("Email", "asc")
        ]

        if (!!pageSize) {
            queries.push(limit(pageSize))
        }

        if (!!isNextPage && !!pageReference) {
            queries.push(startAfter(pageReference))
        }

        if (!isNextPage && !!pageReference) {
            queries.push(endBefore(pageReference))
        }

        if (!!emailFilter && emailFilter != "") {
            queries.push(where("Email", "==", emailFilter))
        }

        return queries;
    }

    const term = (email || "").trim();
    let snapshot = await getDocs(query(usersCollection, ...buildQueries(term)));

    // A busca exata é case-sensitive no Firestore. Os fluxos de gravação agora
    // normalizam o e-mail em minúsculas, mas o admin pode digitar com maiúscula
    // (e docs novos são lowercase): antes de devolver vazio — o "aluno sumiu"
    // dos tickets — tenta o termo em minúsculas.
    const lowered = term.toLowerCase();
    if (!!term && snapshot.empty && lowered !== term) {
        snapshot = await getDocs(query(usersCollection, ...buildQueries(lowered)));
    }

    return snapshot;
}


export const getCountUsers = async (
    {
        email,
    }
) => {
    const usersCollection = collection(firestore, 'users');

    // Firestore não permite '==' e '!=' no mesmo campo. Ao filtrar por e-mail exato,
    // usamos apenas o '==' (a contagem é o nº de docs que casam); caso contrário,
    // contamos todos os alunos que possuem e-mail.
    const term = (email || "").trim();
    if (!term) {
        return getCountFromServer(query(usersCollection, where("Email", "!=", null)));
    }

    // Mesmo fallback case-insensitive do getUsers acima.
    let countSnapshot = await getCountFromServer(query(usersCollection, where("Email", "==", term)));
    const lowered = term.toLowerCase();
    if (countSnapshot.data().count === 0 && lowered !== term) {
        countSnapshot = await getCountFromServer(query(usersCollection, where("Email", "==", lowered)));
    }

    return countSnapshot;
}


// Última apuração dos cards (acesso ativo / sem compras / expirado),
// COMPARTILHADA entre admins e aparelhos. A apuração lê todos os alunos e só
// roda quando alguém clica em "Apurar"; abrir a tela custa 1 leitura deste doc.
// Antes ela ficava só no navegador de quem apurou: outro computador, outra
// admin ou outro navegador abria com "—".
const usersStatsRef = () => doc(firestore, "adminStats", "users");

export const getSharedUsersStats = async () => {
    const snap = await getDoc(usersStatsRef());
    const data = snap.exists() ? snap.data() : null;
    return data && typeof data.active === "number" ? data : null;
}

// Substitui o doc inteiro: uma apuração nova limpa o aviso de defasado.
export const saveSharedUsersStats = (stats) => setDoc(usersStatsRef(), stats);

// Acesso de aluno mudou depois da apuração: os números ficam, com o aviso.
export const markSharedUsersStatsStale = () =>
    setDoc(usersStatsRef(), { stale: true }, { merge: true });

export const getUserById = (id) => {
    const docRef = doc(firestore, "users", id);
    return getDoc(docRef);
}


export const getUserContent = (userId) => {
    return getDocs(collection(firestore, "users", userId, "contents"));
}

// Quantos vídeos o aluno marcou como assistidos. Agregação no servidor: 1
// leitura por aluno (até mil vídeos), em vez de ler TODOS os docs de
// `contents` dele — a tabela e o CSV só mostram a contagem. Índice automático
// de campo único, sem índice composto.
export const getWatchedCount = async (userId) => {
    const snap = await getCountFromServer(
        query(collection(firestore, "users", userId, "contents"), where("isWatched", "==", true))
    );
    return snap.data().count;
}

// Docs atuais de alunos por id, em blocos de 30 (`documentId() in`), na ordem
// recebida. A busca por nome casa os ids no diretório em memória, mas mostra
// o doc relido: o diretório pode ter até meia hora.
export const getUsersByIds = async (ids = []) => {
    const chunks = [];
    for (let i = 0; i < ids.length; i += 30) chunks.push(ids.slice(i, i + 30));
    const byId = new Map();
    await Promise.all(
        chunks.map(async (chunk) => {
            const snap = await getDocs(
                query(collection(firestore, "users"), where(documentId(), "in", chunk))
            );
            snap.forEach((d) => byId.set(d.id, { id: d.id, ...d.data() }));
        })
    );
    return ids.map((id) => byId.get(id)).filter(Boolean);
}

// Datas de criação das contas no Authentication (fonte de verdade da "data de
// cadastro"; o created_at do doc pode ter sido carimbado por um acesso manual).
// Devolve { uid: epochMs }. Falha silenciosa: sem a função publicada, o painel
// segue exibindo o created_at do doc.
export const getAuthCreationDates = async (uids = []) => {
    const unique = [...new Set((uids || []).filter(Boolean))];
    if (!unique.length) return {};

    const callable = httpsCallable(functions, "getAuthCreationDates");
    const creationTimes = {};

    // A função aceita até 1000 uids; 300 por chamada mantém o payload pequeno.
    for (let i = 0; i < unique.length; i += 300) {
        try {
            const response = await callable({ uids: unique.slice(i, i + 300) });
            Object.assign(creationTimes, response?.data?.creationTimes || {});
        } catch (error) {
            console.warn("Não foi possível ler as datas de cadastro do Authentication:", error?.message || error);
            break;
        }
    }

    return creationTimes;
}

export const getHotmartDelayedSubscriptions = (params = {}) => {
    const hotmartGetDelayedSubscriptions = httpsCallable(functions, "hotmartGetDelayedSubscriptions");
    return hotmartGetDelayedSubscriptions(params);
}

export const cancelHotmartSubscriptions = (params = {}) => {
    const hotmartCancelSubscriptions = httpsCallable(functions, "hotmartCancelSubscriptions");
    return hotmartCancelSubscriptions(params);
}